<script>
import { authComputed, userComputed } from '@state/helpers'
import { LEGACY_INFRARED_ENABLED } from '@utils/feature-flags'
import { mayOpenAdminArea } from '@utils/admin-area'
import NavBarRoutes from './nav-bar-routes.vue'

export default {
  components: { NavBarRoutes },
  props: {
    drawer: Boolean,
  },
  emits: ['update:drawer'],
  data() {
    return {
      persistentNavRoutes: [],
    }
  },
  computed: {
    // isAdmin and can come from these spreads (the auth module's getters).
    // See src/utils/roles.js for why they aren't local.
    ...authComputed,
    ...userComputed,
    // Build-time constant surfaced as a computed so the template can read it.
    legacyInfraredEnabled: () => LEGACY_INFRARED_ENABLED,
    // Whoever may open a page of the admin area (src/utils/admin-area.js).
    showAdmin() {
      return this.loggedIn && mayOpenAdminArea(this.can, this.isAdmin)
    },
    // Computeds, not data(), so the titles re-render when the locale switches.
    loggedInNavRoutes() {
      return [
        {
          name: 'locations',
          title: this.$t('nav.locations'),
          icon: '$location',
        },
      ]
    },
    loggedOutNavRoutes() {
      return [
        { name: 'login', title: this.$t('common.logIn'), icon: '$logout' },
      ]
    },
  },
}
</script>

<template>
  <v-navigation-drawer
    :model-value="drawer"
    temporary
    class="nav-drawer"
    @update:model-value="$emit('update:drawer', $event)"
  >
    <v-list density="compact">
      <v-list-item
        :to="{ name: 'home' }"
        prepend-icon="$home"
        :title="$t('nav.home')"
      />
      <v-list-item
        v-if="loggedIn"
        :to="{ name: 'dashboard' }"
        prepend-icon="$dashboard"
        :title="$t('nav.dashboard')"
      />
      <NavBarRoutes :routes="persistentNavRoutes" />
      <NavBarRoutes v-if="loggedIn" :routes="loggedInNavRoutes" />
      <NavBarRoutes v-else :routes="loggedOutNavRoutes" />
      <v-list-item
        v-if="loggedIn"
        href="/pdfs/moves-adds-changes.pdf"
        download="Moves_Adds_Changes.pdf"
        target="_blank"
        rel="noopener noreferrer"
        prepend-icon="$pdfDownload"
        :title="$t('nav.movesAddsChanges')"
      />
      <v-list-item
        v-if="loggedIn"
        :to="{ name: 'how-to' }"
        prepend-icon="$help"
        :title="$t('nav.howTo')"
      />
      <!-- No Infrared or LOTO entry: both are tabs of a location's Devices
           page, as in the app bar (layouts/main.vue). -->
      <!-- Read-only archive from the retired infrared system. infrared.legacy,
           as the route and every legacyIR* query, and flag-gated so it
           disappears with the route (see utils/feature-flags). A nav entry
           outliving its route is a link straight to the 404. -->
      <v-list-item
        v-if="legacyInfraredEnabled && loggedIn && can('infrared.legacy')"
        :to="{ name: 'legacy-infrared' }"
        prepend-icon="$dashboard"
        :title="$t('nav.legacyInfrared')"
      />
      <v-list-item
        v-if="showAdmin"
        :to="{ name: 'admin' }"
        prepend-icon="$lock"
        :title="$t('nav.admin')"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<style scoped>
.nav-drawer {
  /* !important: Vuetify manages this component's own z-index dynamically
     (for overlay stacking order); a plain class rule alone doesn't reliably
     win, unlike the original inline :style binding which did. */
  z-index: 9999 !important;
}
</style>
